import { ReactComponent as InfoIcon } from "../../../../../icons/info-Simulator.svg";
import { useCallback, useEffect, useState } from "react";
import { getBessAnnualSummaryCharts } from "../../../../../Rest_api/RestApi";
import loader from "../../../../../components/recharts/loader.gif";
import PortfolioPiechart from "./PortfolioPiechart";

interface BarChartData {
    re_gen_cost: number;
    non_re_gen_cost: number;
    cost_after_bess: number;
    unit: string;
}

interface PercentageData {
    re_generation: number;
    non_re_generation: number;
}

interface ApiResponse {
    session: string;
    session_id: number;
    scenario: number;
    start_date: string;
    end_date: string;
    bar_chart_data: BarChartData;
    percentage_data: PercentageData;
}

export default function SummaryContributor({
    refreshKey,
    startDate,
    endDate,
    scenarioId,
    setError,
}: {
    refreshKey: number;
    selectedBlock?: any[]; // Not used but kept for prop compatibility if needed
    startDate: string;
    endDate?: string;
    scenarioId: number;
    setError?: any;
}) {
    const [data, setData] = useState<ApiResponse | null>(null);
    const [loading, setLoading] = useState(false);

    const handleFetchData = useCallback(async () => {
        const effectiveEndDate = endDate || startDate;

        if (!startDate || scenarioId === undefined || scenarioId === null) return;

        try {
            setLoading(true);
            const res = await getBessAnnualSummaryCharts({
                start_date: startDate,
                end_date: effectiveEndDate,
                scenario_id: scenarioId.toString(),
            });

            if (res) {
                setData(res);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            if (setError) setError("An error occurred while fetching data.");
        } finally {
            setLoading(false);
        }
    }, [scenarioId, startDate, endDate, setError, refreshKey]);

    useEffect(() => {
        handleFetchData();
    }, [handleFetchData]);

    // Transform data for Cost Chart (Left)
    const costItems = data
        ? [
            { name: "RE Gen Cost", value: data.bar_chart_data.re_gen_cost, color: "#34656D" },
            { name: "Non-RE Gen Cost", value: data.bar_chart_data.non_re_gen_cost, color: "#F1935C" },
            { name: "Cost After BESS", value: data.bar_chart_data.cost_after_bess, color: "#8C8A96" },
        ]
        : [];

    costItems.sort((a, b) => b.value - a.value);

    const maxCostValue = Math.max(...costItems.map((d) => d.value), 0) || 1;
    const costUnit = data?.bar_chart_data.unit || "crores";

    return (
        <div className="col-span-1 md:col-span-12 flex flex-col md:flex-row gap-2 h-full w-full">
            {/* Left Card: Generation Cost */}
            <div className="dash-card border border-[#DFEAF2] bg-white shadow-[rgba(149,157,165,0.2)_0px_8px_24px] flex-1 h-full min-h-0 rounded-2xl p-4 overflow-hidden flex flex-col !min-h-[100px]">
                <div className="flex justify-start items-center gap-2 mb-3 border-b-2 border-[#DFEAF2] pb-2">
                    <h2 className="chartHeading !text-sm w-100 !text-[#34656D]">
                        Generation Cost (Crore)
                    </h2>
                    <span>
                        {/* <InfoIcon className="w-6 h-6 text-gray-500" /> */}
                    </span>
                </div>

                {loading ? (
                    <div className="flex-1 w-full bg-white flex items-center justify-center">
                        <img
                            src={loader}
                            width={200}
                            alt="loader"
                            className="block"
                        />
                    </div>
                ) : (
                    <div className="w-full h-full text-start overflow-hidden flex-1">
                        <div className="space-y-10 w-full h-full overflow-y-auto pr-2 custom-scrollbar">
                            {costItems.map((item, index) => {
                                const barWidthPercent = (item.value / maxCostValue) * 100;
                                const isBarTooSmall = barWidthPercent < 30;

                                const displayValue = item.value.toLocaleString(undefined, { maximumFractionDigits: 2 });
                                const isZero = item.value < 0.01; // Treat very small numbers as 0 for display logic if needed, or strictly check 0
                                const effectiveWidth = isZero ? 0 : barWidthPercent;

                                return (
                                    <div key={index} className="flex items-center justify-between mb-4">
                                        <span className="text-sm text-[#525062] font-medium w-40 truncate" title={item.name}>
                                            {item.name}
                                        </span>
                                        <div className="flex-1 ml-4 relative">
                                            <div className="relative rounded-lg h-7 overflow-hidden">
                                                <div
                                                    className={`absolute top-0 left-0 h-7 flex items-center transition-all duration-500 ${isBarTooSmall ? "justify-start pl-4" : "justify-end pr-4"
                                                        }`}
                                                    style={{ width: `${effectiveWidth}%`, backgroundColor: "#FCE9DE" }}
                                                >
                                                    {!isBarTooSmall && (
                                                        <span className="font-medium text-[10px] text-[#913A08]">
                                                            {displayValue} MWh
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {isBarTooSmall && (
                                                <span
                                                    className="absolute top-0 left-0 h-7 flex items-center font-medium text-[10px] ml-4 text-[#913A08]"
                                                    style={{ left: `${effectiveWidth}%` }}
                                                >
                                                    {displayValue} MWh
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Right Card: Renewable Purchase Obligation */}
            <div className="dash-card border border-[#DFEAF2] bg-white shadow-[rgba(149,157,165,0.2)_0px_8px_24px] flex-1 h-full min-h-0 rounded-2xl p-4 overflow-hidden flex flex-col !min-h-[100px]">
                <div className="flex justify-start items-center gap-2 mb-2 border-b-2 border-[#DFEAF2] pb-2">
                    <h2 className="chartHeading !text-sm w-100 !text-[#34656D]">
                        Renewable Purchase Obligation
                    </h2>
                    <span>
                        {/* <InfoIcon className="w-6 h-6 text-gray-500" /> */}
                    </span>
                </div>

                {loading ? (
                    <div className="flex-1 w-full bg-white flex items-center justify-center">
                        <img
                            src={loader}
                            width={200}
                            alt="loader"
                            className="block"
                        />
                    </div>
                ) : (
                    <div className="w-full h-full text-start overflow-hidden flex-1 flex items-center justify-center">
                        {data && <PortfolioPiechart data={[data.percentage_data]} chartHeight="100%" />}
                    </div>
                )}
            </div>
        </div>
    );
}
